// Employee Tracking Policy — admin-only Settings section.
//
// Volkan direction 2026-05-24: screenshot interval + inactivity timeout must
// live at the company-policy level, not on the individual Cattr user record.
// Cattr's own schema stores these as user-level fields (`screenshots_interval`,
// `computer_time_popup`) — a Cattr design weakness for employer-monitoring
// deployments. We therefore surface a single PEXIFY-owned policy that, in M3,
// a backend service propagates to every Cattr user via the Cattr API + locks
// at every sync window so users cannot self-edit and drift.
//
// M0 simulation discipline (`pol-build-sequence`): the form is purely local
// state. Save fires a toast and persists nothing. M3 Stage 4 SPEC.md Faz 5
// (Notifications + Policies + Routing) wires the TrackingPolicyService that
// translates this surface into Cattr API PATCH calls.
//
// Why not fork Cattr's UI to move these settings: server-application image is
// unbuildable (private base image + private composer/npm modules — see
// reference_cattr_v5_gotchas memory), so any Cattr-side change is impractical.
// Policy lives in PEXIFY, Cattr remains an immutable appliance.

import { useState } from 'react'
import { ShieldCheck, Users } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { employeeTrackingI18n } from '../../i18n/settings/employee-tracking'
import type { SettingsSurface } from './Settings'

type IntervalMinutes = 1 | 5 | 10 | 15 | 30
type InactivitySeconds = 60 | 180 | 300 | 600 | 1200

type IntervalOptionId = 'one' | 'five' | 'ten' | 'fifteen' | 'thirty'
type InactivityOptionId = 'sixty' | 'oneEighty' | 'threeHundred' | 'sixHundred' | 'twelveHundred'

const INTERVAL_OPTIONS: { value: IntervalMinutes; id: IntervalOptionId }[] = [
  { value: 1, id: 'one' },
  { value: 5, id: 'five' },
  { value: 10, id: 'ten' },
  { value: 15, id: 'fifteen' },
  { value: 30, id: 'thirty' },
]

const INACTIVITY_OPTIONS: { value: InactivitySeconds; id: InactivityOptionId }[] = [
  { value: 60, id: 'sixty' },
  { value: 180, id: 'oneEighty' },
  { value: 300, id: 'threeHundred' },
  { value: 600, id: 'sixHundred' },
  { value: 1200, id: 'twelveHundred' },
]

// Mock — gerçek değerler M3'te Cattr API'den fetch edilecek.
// Şu an Cattr DB'de Volkan id=3: screenshots_interval=1, computer_time_popup=300.
const MOCK_AFFECTED_USERS = 1

function PolicyCard({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <header className="border-b border-slate-100 px-5 py-4">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {description && (
          <p className="mt-0.5 text-xs text-slate-500">{description}</p>
        )}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  )
}

function FieldRow({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid grid-cols-[200px_1fr] items-start gap-4 py-3 first:pt-0 last:pb-0">
      <div>
        <label className="text-sm font-medium text-slate-700">{label}</label>
        {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  )
}

export function EmployeeTracking({ surface }: { surface: SettingsSurface }) {
  const t = useT(employeeTrackingI18n)
  const [interval, setInterval] = useState<IntervalMinutes>(10)
  const [inactivity, setInactivity] = useState<InactivitySeconds>(300)
  const [dirty, setDirty] = useState(false)

  // Admin-only — defensively early-return for non-admin surfaces. The Settings
  // chrome already filters via `surfaces: ['admin']`, this is belt-and-braces.
  // (Hooks stay ABOVE this return: hook order must not depend on `surface`.)
  if (surface !== 'admin') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        {t.adminOnly}
      </div>
    )
  }

  const handleSave = () => {
    // M3: PATCH PEXIFY backend `/admin/employee-tracking-policy` → backend
    // TrackingPolicyService → Cattr API PATCH /api/users/edit for every user.
    toast.success(t.toast.title, {
      description: t.toast.description(MOCK_AFFECTED_USERS),
      duration_ms: 5000,
    })
    setDirty(false)
  }

  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-lg font-semibold text-slate-900">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-slate-600">{t.pageDescription}</p>
      </header>

      <PolicyCard
        title={t.cards.screenshot.title}
        description={t.cards.screenshot.description}
      >
        <FieldRow
          label={t.cards.screenshot.intervalLabel}
          hint={t.cards.screenshot.intervalHint}
        >
          <select
            value={interval}
            onChange={(e) => {
              setInterval(Number(e.target.value) as IntervalMinutes)
              setDirty(true)
            }}
            className="w-full max-w-md rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          >
            {INTERVAL_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t.intervalOptions[opt.id]}
              </option>
            ))}
          </select>
        </FieldRow>
      </PolicyCard>

      <PolicyCard
        title={t.cards.idle.title}
        description={t.cards.idle.description}
      >
        <FieldRow
          label={t.cards.idle.inactivityLabel}
          hint={t.cards.idle.inactivityHint}
        >
          <select
            value={inactivity}
            onChange={(e) => {
              setInactivity(Number(e.target.value) as InactivitySeconds)
              setDirty(true)
            }}
            className="w-full max-w-md rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          >
            {INACTIVITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {t.inactivityOptions[opt.id]}
              </option>
            ))}
          </select>
        </FieldRow>
      </PolicyCard>

      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-5 py-3">
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <Users className="h-3.5 w-3.5 text-slate-400" />
          <span>
            {t.impactLabel}{' '}
            <strong className="text-slate-900">{t.impactCount(MOCK_AFFECTED_USERS)}</strong>{' '}
            {t.impactTail}
          </span>
        </div>
        <Button onClick={handleSave} disabled={!dirty}>
          {dirty ? t.save : t.saved}
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-5 py-3 text-xs text-amber-900">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div className="space-y-1">
          <p>
            <strong>{t.simulationNote.badge}</strong> {t.simulationNote.body}
          </p>
          <p>{t.simulationNote.backendNote}</p>
        </div>
      </div>
    </div>
  )
}
